import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import { Bold, Italic, Underline as UndIcon, List, ListOrdered, Quote, Heading2, Heading3, Link2, Undo, Redo, AArrowDown, AArrowUp } from "lucide-react";

const SIZES = ["14px", "16px", "18px", "20px", "24px", "30px", "36px"];

const FontSize = Extension.create({
  name: "fontSize",
  addOptions() { return { types: ["textStyle"] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el) => el.style.fontSize || null,
          renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain().setMark("textStyle", { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

export default function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false, underline: false }),
      TextStyle,
      FontSize,
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Placeholder.configure({ placeholder: "Tulis isi artikel di sini…" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;

  const Btn = ({ onClick, active, children, label }) => (
    <button type="button" onMouseDown={(e) => { e.preventDefault(); onClick(); }} aria-label={label} data-testid={`rte-${label}`}
      className={`p-2 rounded hover:bg-dlh-greenPale transition-colors ${active ? "bg-dlh-green text-white hover:bg-dlh-greenDark" : "text-gray-700"}`}>
      {children}
    </button>
  );

  const addLink = () => {
    const url = window.prompt("Masukkan URL:");
    if (url) editor.chain().focus().setLink({ href: url }).run();
    else editor.chain().focus().unsetLink().run();
  };

  const currentSize = editor.getAttributes("textStyle")?.fontSize || "";
  const stepSize = (dir) => {
    const idx = SIZES.indexOf(currentSize || "16px");
    const base = idx === -1 ? SIZES.indexOf("16px") : idx;
    const next = Math.min(SIZES.length - 1, Math.max(0, base + dir));
    editor.chain().focus().setFontSize(SIZES[next]).run();
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 p-1.5 bg-gray-50">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="bold"><Bold className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="italic"><Italic className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} label="underline"><UndIcon className="h-4 w-4" /></Btn>
        <div className="w-px bg-gray-300 mx-1 self-stretch" />
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="h2"><Heading2 className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="h3"><Heading3 className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} label="ul"><List className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="ol"><ListOrdered className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="quote"><Quote className="h-4 w-4" /></Btn>
        <Btn onClick={addLink} active={editor.isActive("link")} label="link"><Link2 className="h-4 w-4" /></Btn>
        <div className="w-px bg-gray-300 mx-1 self-stretch" />
        {/* Ukuran huruf */}
        <Btn onClick={() => stepSize(-1)} label="perkecil-huruf"><AArrowDown className="h-4 w-4" /></Btn>
        <select value={SIZES.includes(currentSize) ? currentSize : ""} data-testid="rte-font-size"
          onChange={(e) => { const v = e.target.value; if (v) editor.chain().focus().setFontSize(v).run(); else editor.chain().focus().unsetFontSize().run(); }}
          className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-dlh-orange">
          <option value="">Ukuran</option>
          <option value="14px">Kecil</option>
          <option value="16px">Normal</option>
          <option value="18px">Agak Besar</option>
          <option value="20px">Besar</option>
          <option value="24px">Lebih Besar</option>
          <option value="30px">Judul Kecil</option>
          <option value="36px">Judul Besar</option>
        </select>
        <Btn onClick={() => stepSize(1)} label="perbesar-huruf"><AArrowUp className="h-4 w-4" /></Btn>
        <div className="w-px bg-gray-300 mx-1 self-stretch" />
        <Btn onClick={() => editor.chain().focus().undo().run()} label="undo"><Undo className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} label="redo"><Redo className="h-4 w-4" /></Btn>
      </div>
      <EditorContent editor={editor} className="rte-content" data-testid="rte-editor" />
    </div>
  );
}
